import React from "react";
import type Widget from "@/model/widget";
import WidgetCard from "./widget-card";
import { CircleArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ScreenRouteNames from "@/constants/screen-route-names";
import { Button } from "@/components/ui/button";
import { useBalanceBillList } from "@/hooks/use-balance-bill";
import type { BalanceBillListResponseDataType } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/hooks/use-query-client";
import { QueryKeys } from "@/constants";
import useUserInfo from "@/hooks/use-user-info";

type BalanceBillWidgetProps = {
  className?: string;
  widget: Widget;
};

const BalanceBillWidget: React.FunctionComponent<BalanceBillWidgetProps> = ({
  widget,
}) => {
  const MAX_NO_OF_BILLS_TO_SHOW = 5;
  const userInfo = useUserInfo();
  const navigate = useNavigate();
  const balanceBills = useBalanceBillList();

  const showBalanceBill = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ])?.data?.[0]?.showBilling;

  const navigateToBalanceBill = () => {
    const targetPath = `/${ScreenRouteNames.BALANCE_BILL.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const footerContent = (
    /* 2.1.1 Keyboard — A: the click handler sat on a wrapper <div> while the <Button>
       inside it had none — so the only keyboard-focusable element did nothing, and the
       thing that actually navigated could not be reached. Moving the handler onto the
       button makes the control that looks actionable the one that is. */
    <div className="flex flex-row justify-between">
      <Button variant="link" className="w-full" onClick={navigateToBalanceBill}>
        View All <CircleArrowRight aria-hidden="true" className="ml-1" />
      </Button>
    </div>
  );

  const renderWidget = () => {
    const bills = balanceBills?.data;
    const hasBills = Array.isArray(bills) && bills?.length > 0;

    // if (balanceBills.isError)
    //   return (
    //     <SomethingWentWrong message={getErrorMessage(balanceBills.error)} />
    //   );

    return (
      <div className="space-y-5">
        <div className="bg-gray-100 p-3">
          <h4 className="text-center text-medium font-medium">
            Submitted Bills
          </h4>
        </div>

        {!hasBills && (
          <p className="py-8 text-center text-sm font-medium text-gray-500">
            No balance bills have been submitted.
          </p>
        )}

        {hasBills && (
          <div>
            {bills
              .slice(0, MAX_NO_OF_BILLS_TO_SHOW)
              .map((bill: BalanceBillListResponseDataType, index: number) => {
                const uploadedDate = bill?.submittedAt;

                return uploadedDate ? (
                  <p
                    key={index}
                    className={`${index !== 0 ? "border-t border-gray-300 pt-3" : ""} my-3 text-sm font-medium`}
                  >
                    Submitted: {uploadedDate}
                  </p>
                ) : null;
              })}
          </div>
        )}
      </div>
    );
  };

  const skeletonLoader = (
    <div className="space-y-5">
      <Skeleton className="w-100 h-4 bg-gray-200" />
      <Skeleton className="w-100 h-4 bg-gray-200" />
      <Skeleton className="w-100 h-4 bg-gray-200" />
      <Skeleton className="w-100 h-4 bg-gray-200" />
      <Skeleton className="w-100 h-4 bg-gray-200" />
    </div>
  );

  if (!showBalanceBill) return null;

  return (
    <WidgetCard title={widget?.widgetDisplayName} footerContent={footerContent}>
      {balanceBills?.isLoading ? skeletonLoader : renderWidget()}
    </WidgetCard>
  );
};

export default BalanceBillWidget;
